import React, { useEffect, useState } from 'react';
import {
  Paper, Typography, Box, Button, Grid, CircularProgress, Alert,
  Select, InputLabel, MenuItem, FormControl, useTheme, useMediaQuery, IconButton
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../../api';

const STORAGE_KEY_CLASSE = 'badminton_selected_classe';

export default function DefiBadSettings({ navigateToMatches }) {
  const navigate = useNavigate();
  const location = useLocation();
  const initialFromState = location?.state?.selectedClasse;

  const [classes, setClasses] = useState([]);
  const [selectedClasse, setSelectedClasse] = useState('');
  const [ranking, setRanking] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [errorMsg, setErrorMsg] = useState(null);
  const [successMsg, setSuccessMsg] = useState(null);
  const defaultTopPoints = 40;

  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up('md'));
  const isSmUp = useMediaQuery(theme.breakpoints.up('sm'));
  const selectMinWidth = isMdUp ? 360 : (isSmUp ? 280 : '100%');

  useEffect(() => {
    const stored = localStorage.getItem(STORAGE_KEY_CLASSE);
    const initial = initialFromState || stored || '';
    if (initial) {
      setSelectedClasse(initial);
    }
  }, [initialFromState]);

  useEffect(() => {
    let mounted = true;
    api.get('/classes')
      .then(res => {
        if (!mounted) return;
        setClasses(Array.isArray(res.data) ? res.data : []);
      })
      .catch(err => {
        console.warn('Erreur chargement classes', err);
        setClasses([]);
      });
    return () => { mounted = false; };
  }, []);

  useEffect(() => {
    setRanking([]);
    setLoading(false);
    setErrorMsg(null);
    setSuccessMsg(null);

    if (!selectedClasse) {
      localStorage.removeItem(STORAGE_KEY_CLASSE);
      return;
    }

    localStorage.setItem(STORAGE_KEY_CLASSE, selectedClasse);

    const timer = setTimeout(() => {
      refreshEleves({ sortByPoints: false, animate: false });
    }, 50);

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClasse]);

  const refreshEleves = async (options = { sortByPoints: false, animate: false }) => {
    if (!selectedClasse) return [];
    setLoading(true);
    setErrorMsg(null);

    try {
      const ref = await api.get(`/badminton/eleves?classe=${encodeURIComponent(selectedClasse)}`);
      const list = Array.isArray(ref.data) ? ref.data : [];

      const hasRankIndex = list.some(e => e.rankIndex !== undefined && e.rankIndex !== null);
      const ordered = hasRankIndex
        ? list.slice().sort((a, b) => {
          const ai = (a.rankIndex === undefined || a.rankIndex === null) ? Number.MAX_SAFE_INTEGER : a.rankIndex;
          const bi = (b.rankIndex === undefined || b.rankIndex === null) ? Number.MAX_SAFE_INTEGER : b.rankIndex;
          return ai - bi;
        })
        : list.slice();

      const computed = ordered.map((e, i) => {
        const pts = (typeof e.points === 'number') ? e.points : Math.max(0, defaultTopPoints - i);
        return { ...e, points: pts };
      });

      const final = options.sortByPoints
        ? computed.slice().sort((a, b) => {
          const diff = (b.points || 0) - (a.points || 0);
          if (diff !== 0) return diff;
          const ai = (a.rankIndex === undefined || a.rankIndex === null) ? Number.MAX_SAFE_INTEGER : a.rankIndex;
          const bi = (b.rankIndex === undefined || b.rankIndex === null) ? Number.MAX_SAFE_INTEGER : b.rankIndex;
          if (ai !== bi) return ai - bi;
          return (a.nom || '').localeCompare(b.nom || '');
        })
        : computed;

      setRanking(final);
      return final;
    } catch (err) {
      console.error('refreshEleves error', err);
      setErrorMsg('Erreur chargement élèves — voir console');
      setRanking([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  const onDragEnd = (result) => {
    if (!result.destination) return;
    const src = result.source.index;
    const dest = result.destination.index;
    if (src === dest) return;

    const next = Array.from(ranking);
    const [moved] = next.splice(src, 1);
    next.splice(dest, 0, moved);

    const reindexed = next.map((e, i) => ({
      ...e,
      points: (typeof e.points === 'number' ? e.points : Math.max(0, defaultTopPoints - i))
    }));

    setRanking(reindexed);
    setSuccessMsg(null);
    setErrorMsg(null);
  };

  const handleSaveOrder = async () => {
    if (!selectedClasse) return setErrorMsg('Sélectionne d\'abord une classe.');
    if (ranking.length === 0) return setErrorMsg('Aucun élève à sauvegarder.');

    setSaving(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const orderedIds = ranking.map(r => r._id);
      const res = await api.post(`/badminton/classe/${selectedClasse}/save-order`, { orderedIds });

      if (res.status >= 200 && res.status < 300) {
        setSuccessMsg('Ordre sauvegardé.');
        await refreshEleves({ sortByPoints: false, animate: false });
      } else {
        setErrorMsg('Erreur lors de la sauvegarde de l\'ordre.');
      }
    } catch (err) {
      console.error('handleSaveOrder error', err);
      setErrorMsg(err?.response?.data?.error || err?.message || 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const handleAssignPoints = async (basePoints = defaultTopPoints) => {
    if (!selectedClasse) return setErrorMsg('Sélectionne d\'abord une classe.');
    if (ranking.length === 0) return setErrorMsg('Aucun élève à classer.');

    setSaving(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const orderedIds = ranking.map(r => r._id);
      const res = await api.post(`/badminton/classe/${selectedClasse}/assign-points`, { orderedIds, basePoints });

      if (res.status >= 200 && res.status < 300) {
        setSuccessMsg('Points attribués et ordre sauvegardé.');
        await refreshEleves({ sortByPoints: false, animate: false });
      } else {
        setErrorMsg('Erreur lors de l\'attribution des points.');
      }
    } catch (err) {
      console.error('handleAssignPoints error', err);
      setErrorMsg(err?.response?.data?.error || err?.message || 'Erreur lors de l\'attribution');
    } finally {
      setSaving(false);
    }
  };

  const handleResetPoints = async () => {
    if (!selectedClasse) return setErrorMsg('Sélectionne d\'abord une classe.');
    if (!window.confirm('Remettre tous les points à 0 pour cette classe ?')) return;

    setSaving(true);
    setErrorMsg(null);
    setSuccessMsg(null);

    try {
      const res = await api.post(`/badminton/classe/${selectedClasse}/reset-points`);

      if (res.status >= 200 && res.status < 300) {
        setSuccessMsg('Points réinitialisés.');
        await refreshEleves({ sortByPoints: false, animate: false });
      } else {
        setErrorMsg('Erreur lors du reset.');
      }
    } catch (err) {
      console.error('handleResetPoints error', err);
      setErrorMsg(err?.response?.data?.error || err?.message || 'Erreur lors du reset');
    } finally {
      setSaving(false);
    }
  };

  const goToBadmintonHome = () => {
    navigate('/apps/badminton');
  };

  return (
    <Paper sx={{ p: 2, mt: 3 }}>
      <Grid container alignItems="center" spacing={1} sx={{ mb: 2 }}>
        <Grid item xs={2} sx={{ textAlign: 'left' }}>
          <IconButton
            aria-label="Retour accueil Badminton"
            onClick={goToBadmintonHome}
            size="large"
            sx={{ color: '#6b6b6b' }}
            title="Retour à l'accueil Badminton"
          >
            <ArrowBackIcon />
          </IconButton>
        </Grid>
        <Grid item xs={8} sx={{ textAlign: 'center' }}>
          <Typography variant="h6">RÉGLAGES MATCHS EN DÉFIS</Typography>
        </Grid>
        <Grid item xs={2} />
      </Grid>

      <Box sx={{ mt: 2 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, p: 2, bgcolor: '#f0f8ff', borderRadius: 1 }}>
          📋 <strong>Instructions :</strong> Ici vous classez vos élèves suite à une montante-descendante ou autre système de pré-classement par exemple.
          Glissez-déposez les élèves de la classe pour effectuer le classement puis cliquez sur "Validez le classement et attribuer les points".
          L'appli attribuera les points automatiquement (40 pour le 1er puis -1 pour chaque élève en suivant : 39, 38, 37...).
          Lancez ensuite les matchs par défis pour commencer le tournoi.
        </Typography>

        <FormControl fullWidth sx={{ minWidth: selectMinWidth, mb: 3 }}>
          <InputLabel id="classe-select-mgmt">Classe</InputLabel>
          <Select
            labelId="classe-select-mgmt"
            value={selectedClasse}
            label="Classe"
            onChange={(e) => setSelectedClasse(e.target.value)}
          >
            <MenuItem value="">Sélectionner</MenuItem>
            {classes.map(c => <MenuItem key={c._id} value={c._id}>{c.nom || c.name}</MenuItem>)}
          </Select>
        </FormControl>

        {selectedClasse && (
          <>
            <Grid container spacing={2} sx={{ mb: 3 }}>
              <Grid item>
                <Button
                  variant="contained"
                  onClick={() => handleAssignPoints(defaultTopPoints)}
                  disabled={saving || ranking.length === 0}
                  sx={{ bgcolor: '#C8E6C9', color: '#0b3d14', '&:hover': { bgcolor: '#B2DFBB' } }}
                >
                  {saving ? 'En cours...' : 'Valider classement & attribuer points'}
                </Button>
              </Grid>

              <Grid item>
                <Button
                  variant="outlined"
                  onClick={handleSaveOrder}
                  disabled={saving || ranking.length === 0}
                  sx={{ borderColor: '#B3E5FC', color: '#155e75', '&:hover': { backgroundColor: '#E8F7FF' } }}
                >
                  Sauver l'ordre
                </Button>
              </Grid>

              <Grid item>
                <Button
                  variant="outlined"
                  color="error"
                  onClick={handleResetPoints}
                  disabled={saving || ranking.length === 0}
                  sx={{ borderColor: '#FFDAB9', color: '#6b3d00', '&:hover': { backgroundColor: '#FFF2E6' } }}
                >
                  Reset points
                </Button>
              </Grid>

              <Grid item>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={() => navigateToMatches && navigateToMatches(selectedClasse)}
                  disabled={ranking.length === 0}
                  sx={{ bgcolor: '#7b6cff', color: '#fff', '&:hover': { bgcolor: '#6a5ae0' } }}
                >
                  🏸 Lancer les matchs
                </Button>
              </Grid>
            </Grid>

            {errorMsg && <Box sx={{ mb: 2 }}><Alert severity="error">{errorMsg}</Alert></Box>}
            {successMsg && <Box sx={{ mb: 2 }}><Alert severity="success">{successMsg}</Alert></Box>}

            {loading ? (
              <Box sx={{ py: 2, display: 'flex', justifyContent: 'center' }}><CircularProgress /></Box>
            ) : (
              <Box sx={{ my: 2 }}>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Classement actuel (glisser-déposer) - {ranking.length} élève{ranking.length > 1 ? 's' : ''}
                </Typography>
                {ranking.length === 0 ? (
                  <Alert severity="info">Aucun élève trouvé dans cette classe.</Alert>
                ) : (
                  <DragDropContext onDragEnd={onDragEnd}>
                    <Droppable droppableId="manage-ranking-droppable">
                      {(provided) => (
                        <div ref={provided.innerRef} {...provided.droppableProps}>
                          {ranking.map((r, i) => (
                            <Draggable key={String(r._id)} draggableId={String(r._id)} index={i}>
                              {(dragProvided, snapshot) => (
                                <Box
                                  ref={dragProvided.innerRef}
                                  {...dragProvided.draggableProps}
                                  {...dragProvided.dragHandleProps}
                                  sx={{
                                    p: 1, mb: 1, border: '1px solid #eee', borderRadius: 1,
                                    display: 'flex', justifyContent: 'space-between',
                                    background: snapshot.isDragging ? '#fafafa' : '#fff',
                                    boxShadow: snapshot.isDragging ? '0 6px 18px rgba(0,0,0,0.08)' : 'none',
                                    transition: 'transform 300ms ease, box-shadow 200ms ease'
                                  }}
                                >
                                  <Typography>{i + 1}. {r.prenom} {r.nom}</Typography>
                                  <Typography>{r.points} pts</Typography>
                                </Box>
                              )}
                            </Draggable>
                          ))}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </DragDropContext>
                )}
              </Box>
            )}
          </>
        )}
      </Box>
    </Paper>
  );
}